import { ClerkAxiosSetup } from '@/api/ClerkAxiosSetup';
import { CACHE_TIME } from '@/api/client';
import { Toaster } from '@/components/design-system/sooner';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { clerkConfig } from '@/config/clerk.config';
import { isDarkModeEnabled } from '@/consts/featureFlags';
import { env } from '@/env/env';
import { useMockServiceWorker } from '@/hooks/useMockServiceWorker';
import { LocaleStoreSyncProvider } from '@/i18n/LocaleStoreSyncProvider';
import { HsqeProvider } from '@/modules/HSQE/providers/HsqeProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { FullStoryProvider } from '@/providers/FullStoryProvider';
import { IntercomProvider } from '@/providers/IntercomProvider';
import { MatchMediaContextProvider } from '@/providers/MatchMediaProvider';
import { MockAuthProvider } from '@/providers/MockAuthProvider';
import { OrganizationProvider } from '@/providers/OrganizationProvider';
import { router } from '@/routing/router';
import { ClerkProvider } from '@clerk/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { RouterProvider } from 'react-router/dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: CACHE_TIME.STALE,
      gcTime: CACHE_TIME.GC,
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

const SharedProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider
    attribute="data-theme"
    defaultTheme="light"
    enableSystem={isDarkModeEnabled}
    forcedTheme={isDarkModeEnabled ? undefined : 'light'}
    disableTransitionOnChange
  >
    <MatchMediaContextProvider>
      {children}
      <Toaster richColors />
      <ReactTooltip
        id="app-tooltip"
        delayShow={300}
        border="1px solid var(--color-border)"
        opacity={1}
        style={{
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-typography-900)',
          borderRadius: '6px',
          padding: '4px 8px',
          fontSize: '12px',
          zIndex: 9999,
          boxShadow: 'var(--shadow-medium)',
          maxWidth: '400px'
        }}
      />
    </MatchMediaContextProvider>
  </ThemeProvider>
);

export const Providers: React.FC = () => {
  const { isReady } = useMockServiceWorker();

  if (!isReady) {
    return null;
  }

  if (env.VITE_MOCK_MODE) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MockAuthProvider>
            <HsqeProvider>
              <SharedProviders>
                <RouterProvider router={router} />
              </SharedProviders>
            </HsqeProvider>
          </MockAuthProvider>
          <LocaleStoreSyncProvider />
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ClerkProvider {...clerkConfig}>
        <OrganizationProvider>
          <ClerkAxiosSetup>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <FullStoryProvider>
                  <IntercomProvider>
                    <HsqeProvider>
                      <SharedProviders>
                        <RouterProvider router={router} />
                      </SharedProviders>
                    </HsqeProvider>
                  </IntercomProvider>
                </FullStoryProvider>
              </AuthProvider>
              <LocaleStoreSyncProvider />
            </QueryClientProvider>
          </ClerkAxiosSetup>
        </OrganizationProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
};
